// TagSeriesChart — line chart for a single numeric tag's series.
//
// Renders the typed GetTagSeries RPC response through the shadcn chart
// primitives (Recharts under the hood) so it themes via the app's CSS
// tokens and matches the rest of the Tailwind/shadcn UI.

import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { useGetTagSeriesQuery, type TagRecord } from '@axilon-apps/domains-ontology-ui-state'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from '../../components/ui/chart'
import { cn } from '../../lib/utils'

export interface TagSeriesChartProps {
  tag: TagRecord
  // Lookback window in seconds; bucket width derived to keep ≲ 200 points.
  windowSecs: number
  className?: string
}

// Aim for a chart resolution that's high enough to look smooth but well below
// the server's 1000-bucket cap. 200 points across the window is a good
// compromise for a panel-sized chart on laptop screens.
const TARGET_BUCKETS = 200

function deriveBucketSecs(windowSecs: number): number {
  return Math.max(1, Math.floor(windowSecs / TARGET_BUCKETS))
}

// Tick/label formatter: drop the date for sub-day windows (just HH:MM) and
// include the month/day once the window spans multiple days.
function makeTimeFormatter(windowSecs: number): (ms: number) => string {
  const multiDay = windowSecs > 24 * 3600
  return (ms: number) => {
    const d = new Date(ms)
    if (Number.isNaN(d.getTime())) return ''
    return multiDay
      ? d.toLocaleString([], {
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
}

const TagSeriesChart = ({ tag, windowSecs, className }: TagSeriesChartProps) => {
  const bucketSecs = deriveBucketSecs(windowSecs)
  const { data, isLoading, error } = useGetTagSeriesQuery({
    parent: tag.name,
    windowSecs,
    bucketSecs
  })

  const points = useMemo(
    () => (data?.points ?? []).map((p) => ({ time: new Date(p.time).getTime(), value: p.avg })),
    [data]
  )

  const config = useMemo(
    () =>
      ({
        value: { label: tag.displayLabel || tag.tagPath, color: 'hsl(var(--chart-1))' }
      }) satisfies ChartConfig,
    [tag.displayLabel, tag.tagPath]
  )
  const fmtTime = useMemo(() => makeTimeFormatter(windowSecs), [windowSecs])
  const chartClassName = cn('h-80 w-full', className)

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center', chartClassName)}>
        <Loader2 className="size-4 animate-spin" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded-md bg-st-down-bg p-3 text-sm text-st-down">
        Failed to load series: {String(error)}
      </div>
    )
  }
  if (points.length === 0) {
    return (
      <div className={cn('flex items-center justify-center', chartClassName)}>
        <span className="text-sm text-muted-foreground">No readings in the selected window.</span>
      </div>
    )
  }

  return (
    <ChartContainer config={config} className={cn('aspect-auto', chartClassName)}>
      <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="time"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          tickFormatter={fmtTime}
          tickLine={false}
          axisLine={false}
          minTickGap={40}
          tickMargin={8}
        />
        <YAxis width={44} tickLine={false} axisLine={false} tickMargin={4} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) => fmtTime(Number(payload?.[0]?.payload?.time))}
            />
          }
        />
        <Line
          dataKey="value"
          type="monotone"
          stroke="var(--color-value)"
          strokeWidth={2}
          // A change-only historian can return one or two buckets for a
          // steady tag; a line through <3 points draws nothing, so mark them.
          dot={points.length < 3}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartContainer>
  )
}

export default TagSeriesChart
